import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import apiClient from '@/lib/api/client';
import { usePins, togglePin } from './PinnedPages';

// ── Recursive main-nav tree (CR #113, deepened per Erik 2026-07-30) ─────────
// The main menu is a folder navigator: every root expands to its record list,
// every record to its child pages, and those to actual records/files —
// Clients › client › Jobs › job › Docs & Photos › the file itself.
// Every level is lazy-loaded on first expand. Rendered by BOTH the mobile
// drawer (light) and the desktop taskbar tray (dark) from TREE_ROOT_LOADERS.

interface TreeNode {
  key: string;
  label: string;
  sub?: string;
  href?: string;           // leaf/branch: navigate in-app
  externalHref?: string;   // file leaf: open in a new tab
  loader?: () => Promise<TreeNode[]>; // branch: lazy children
}

const list = (d: unknown): Record<string, unknown>[] => {
  const x = d as { data?: unknown };
  const inner = (x && typeof x === 'object' && 'data' in x ? x.data : d) as unknown;
  return Array.isArray(inner) ? inner : [];
};

// ── Job subtree: sections, with records/files under the data-backed ones ───
const jobChildren = (jobId: number, base: string, clientId?: number): TreeNode[] => [
  { key: `job-${jobId}-overview`, label: 'Overview', href: base },
  {
    key: `job-${jobId}-documents`, label: 'Docs & Photos', href: `${base}/documents`,
    loader: async () => {
      const r = await apiClient.get(`/jobs/${jobId}/documents`);
      return list(r.data).map(d => ({
        key: `job-${jobId}-doc-${d.id}`,
        label: String(d.label || d.file_name || `File ${d.id}`),
        externalHref: `/${String(d.file_path || '').replace(/^\//, '')}`,
      }));
    },
  },
  {
    key: `job-${jobId}-quotes`, label: 'Quotes & Estimates', href: `${base}/mlestimates`,
    loader: async () => {
      const r = await apiClient.get('/quotes', { params: { job_id: jobId, limit: 100 } });
      const rows = (r.data?.data?.quotes ?? []) as { id: number; quote_number?: string; title?: string; status?: string }[];
      return rows.map(q => ({
        key: `job-${jobId}-quote-${q.id}`,
        label: [q.quote_number, q.title].filter(Boolean).join(' · ') || `Quote ${q.id}`,
        href: clientId ? `/clients/${clientId}/jobs/${jobId}/quotes/${q.id}` : `/quotes/${q.id}`,
      }));
    },
  },
  {
    key: `job-${jobId}-invoices`, label: 'Invoices', href: `${base}/invoices`,
    loader: async () => {
      const r = await apiClient.get('/invoices', { params: { job_id: jobId, limit: 100 } });
      const rows = (r.data?.data?.invoices ?? []) as { id: number; invoice_number?: string; client_id?: number; status?: string }[];
      return rows.map(inv => ({
        key: `job-${jobId}-inv-${inv.id}`,
        label: `${inv.invoice_number || `Invoice ${inv.id}`}${inv.status ? ` (${inv.status})` : ''}`,
        href: (clientId || inv.client_id)
          ? `/clients/${clientId || inv.client_id}/jobs/${jobId}/invoices/${inv.id}`
          : `${base}/invoices`,
      }));
    },
  },
  {
    key: `job-${jobId}-logs`, label: 'Daily Logs', href: `${base}/logs`,
    loader: async () => {
      const r = await apiClient.get(`/jobs/${jobId}/daily-logs`);
      return list(r.data).map(l => ({
        key: `job-${jobId}-log-${l.id}`,
        label: String(l.log_date).slice(0, 10),
        href: `${base}/logs`,
      }));
    },
  },
  {
    key: `job-${jobId}-agreements`, label: 'Agreements', href: `${base}/agreements`,
    loader: async () => {
      const r = await apiClient.get('/agreements', { params: { job_id: jobId } });
      return list(r.data).map(a => ({
        key: `job-${jobId}-agr-${a.id}`,
        label: String(a.title || a.agreement_number || `Agreement ${a.id}`),
        href: (clientId || a.client_id)
          ? `/clients/${clientId || a.client_id}/agreements/${a.id}`
          : `/agreements/${a.id}`,
      }));
    },
  },
  { key: `job-${jobId}-schedule`, label: 'Schedule', href: `${base}/schedule` },
  { key: `job-${jobId}-tasks`, label: 'Tasks', href: `${base}/tasks` },
  { key: `job-${jobId}-expenses`, label: 'Expenses', href: `${base}/expenses` },
  { key: `job-${jobId}-notes`, label: 'Notes', href: `${base}/notes` },
];

const jobNode = (j: { id: number; title?: string; job_number?: string; jobNumber?: string; client_id?: number; clientId?: number }): TreeNode => {
  const clientId = j.client_id ?? j.clientId;
  const base = clientId ? `/clients/${clientId}/jobs/${j.id}` : `/jobs/${j.id}`;
  return {
    key: `job-${j.id}`,
    label: j.title || `Job ${j.job_number || j.jobNumber || j.id}`,
    href: base,
    loader: async () => jobChildren(j.id, base, clientId),
  };
};

const quoteLeaf = (q: Record<string, unknown>, prefix: string): TreeNode => ({
  key: `${prefix}-quote-${q.id}`,
  label: [q.quote_number, q.title].filter(Boolean).join(' · ') || `Quote ${q.id}`,
  href: `/quotes/${q.id}`,
});

const invoiceLeaf = (inv: Record<string, unknown>, prefix: string): TreeNode => ({
  key: `${prefix}-inv-${inv.id}`,
  label: `${inv.invoice_number || `Invoice ${inv.id}`}${inv.status ? ` (${inv.status})` : ''}`,
  href: inv.client_id
    ? (inv.job_id
        ? `/clients/${inv.client_id}/jobs/${inv.job_id}/invoices/${inv.id}`
        : `/clients/${inv.client_id}/invoices/${inv.id}`)
    : '/invoices',
});

// ── Client subtree: every section of the client detail page ────────────────
const clientChildren = (c: { id: number }): TreeNode[] => [
  { key: `client-${c.id}-contact`, label: 'Contact Info', href: `/clients/${c.id}/contact` },
  {
    key: `client-${c.id}-jobs`, label: 'Jobs', href: `/clients/${c.id}/jobs`,
    loader: async () => {
      // NB: the jobs list endpoint takes camelCase `clientId` — `client_id` is
      // silently ignored and returns every job (the original #113 bug).
      const jr = await apiClient.get('/jobs', { params: { clientId: c.id, limit: 100 } });
      const jd = jr.data?.data ?? jr.data;
      const jobs = (Array.isArray(jd) ? jd : jd?.jobs ?? []) as Parameters<typeof jobNode>[0][];
      return jobs.map(j => jobNode({ ...j, client_id: c.id }));
    },
  },
  {
    key: `client-${c.id}-quotes`, label: 'Quotes', href: `/clients/${c.id}/quotes`,
    loader: async () => {
      const r = await apiClient.get('/quotes', { params: { client_id: c.id, limit: 100 } });
      const rows = (r.data?.data?.quotes ?? []) as Record<string, unknown>[];
      return rows.map(q => quoteLeaf(q, `client-${c.id}`));
    },
  },
  { key: `client-${c.id}-estimates`, label: 'Estimates', href: `/clients/${c.id}/estimates` },
  {
    key: `client-${c.id}-invoices`, label: 'Invoices', href: `/clients/${c.id}/invoices`,
    loader: async () => {
      const r = await apiClient.get(`/invoices/client/${c.id}`);
      return list(r.data).map(inv => invoiceLeaf({ ...inv, client_id: c.id }, `client-${c.id}`));
    },
  },
  { key: `client-${c.id}-documents`, label: 'Documents', href: `/clients/${c.id}/documents` },
  { key: `client-${c.id}-notes`, label: 'Notes', href: `/clients/${c.id}/notes` },
];

async function loadClients(): Promise<TreeNode[]> {
  const r = await apiClient.get('/clients');
  const d = r.data?.data ?? r.data;
  const rows = (Array.isArray(d) ? d : d?.clients ?? []) as { id: number; name?: string; company?: string }[];
  return rows.map(c => ({
    key: `client-${c.id}`,
    label: c.name || c.company || `Client ${c.id}`,
    href: `/clients/${c.id}`,
    loader: async () => clientChildren(c),
  }));
}

async function loadJobs(): Promise<TreeNode[]> {
  const r = await apiClient.get('/jobs', { params: { limit: 200 } });
  const d = r.data?.data ?? r.data;
  const jobs = (Array.isArray(d) ? d : d?.jobs ?? []) as Parameters<typeof jobNode>[0][];
  return jobs.map(jobNode);
}

const EMP_SECTIONS: [string, string][] = [
  ['profile', 'Profile'], ['jobs', 'Jobs'], ['schedule', 'Schedule'], ['timesheets', 'Timesheets'],
  ['pay', 'Pay'], ['timeoff', 'Time Off'], ['paystubs', 'Paystubs'], ['documents', 'Documents'],
];

async function loadEmployees(): Promise<TreeNode[]> {
  const r = await apiClient.get('/employees');
  const d = r.data?.data ?? r.data;
  const rows = (Array.isArray(d) ? d : d?.employees ?? []) as { id: number; name?: string }[];
  return rows.map(e => ({
    key: `emp-${e.id}`,
    label: e.name || `Employee ${e.id}`,
    href: `/employees/${e.id}`,
    loader: async () => EMP_SECTIONS.map(([k, l]) => ({ key: `emp-${e.id}-${k}`, label: l, href: `/employees/${e.id}/${k}` })),
  }));
}

async function loadCrews(): Promise<TreeNode[]> {
  const r = await apiClient.get('/crews');
  const rows = list(r.data) as { id: number; name?: string }[];
  // CR #160–163: Employees, Timesheets, Toolbox Talks and Crew Board moved out
  // of the top-level menu — they now live as sub-items under Crews, ahead of
  // the crew list. Employees keeps its full expandable tree.
  const managementNodes: TreeNode[] = [
    { key: 'crews-employees', label: 'Employees', href: '/employees', loader: loadEmployees },
    { key: 'crews-timesheets', label: 'Timesheets', href: '/timesheets' },
    { key: 'crews-toolbox', label: 'Toolbox Talks', href: '/toolbox-talks' },
    { key: 'crews-board', label: 'Crew Board', href: '/crew-board' },
  ];
  return [...managementNodes, ...rows.map(cr => ({
    key: `crew-${cr.id}`,
    label: cr.name || `Crew ${cr.id}`,
    href: `/crews/${cr.id}`,
    loader: async () => {
      // Members come back as user ids — map them onto employee pages.
      const [cd, er] = await Promise.all([
        apiClient.get(`/crews/${cr.id}`),
        apiClient.get('/employees'),
      ]);
      const members = ((cd.data?.data?.members ?? []) as { id: number; name?: string }[]);
      const ed = er.data?.data ?? er.data;
      const emps = (Array.isArray(ed) ? ed : ed?.employees ?? []) as { id: number; user_id?: number; name?: string }[];
      const byUser = new Map(emps.filter(e => e.user_id).map(e => [e.user_id!, e]));
      return members.map(m => {
        const emp = byUser.get(m.id);
        return {
          key: `crew-${cr.id}-m-${m.id}`,
          label: emp?.name || m.name || `Member ${m.id}`,
          href: emp ? `/employees/${emp.id}` : `/crews/${cr.id}`,
        };
      });
    },
  }))];
}

async function loadQuotes(): Promise<TreeNode[]> {
  const r = await apiClient.get('/quotes', { params: { limit: 200 } });
  const rows = (r.data?.data?.quotes ?? []) as Record<string, unknown>[];
  return rows.map(q => quoteLeaf(q, 'root'));
}

async function loadInvoices(): Promise<TreeNode[]> {
  const r = await apiClient.get('/invoices', { params: { limit: 200 } });
  const rows = (r.data?.data?.invoices ?? []) as Record<string, unknown>[];
  return rows.map(inv => invoiceLeaf(inv, 'root'));
}

async function loadAgreements(): Promise<TreeNode[]> {
  const r = await apiClient.get('/agreements');
  return list(r.data).map(a => ({
    key: `agr-${a.id}`,
    label: String(a.title || a.agreement_number || `Agreement ${a.id}`),
    sub: String(a.client_display_name || ''),
    href: `/agreements/${a.id}`,
  }));
}

async function loadVendors(): Promise<TreeNode[]> {
  const r = await apiClient.get('/vendors');
  return list(r.data).map(v => ({
    key: `vendor-${v.id}`,
    label: String(v.name || `Vendor ${v.id}`),
    href: `/vendors/${v.id}`,
  }));
}

async function loadPurchaseOrders(): Promise<TreeNode[]> {
  const r = await apiClient.get('/purchase-orders');
  return list(r.data).map(po => ({
    key: `po-${po.id}`,
    label: `${po.po_number || `PO ${po.id}`}${po.vendor_display || po.vendor_name ? ` · ${po.vendor_display || po.vendor_name}` : ''}`,
    href: '/purchase-orders',
  }));
}

// Documents root: a folder per job, expanding to the actual files.
async function loadDocumentFolders(): Promise<TreeNode[]> {
  const r = await apiClient.get('/jobs', { params: { limit: 200 } });
  const d = r.data?.data ?? r.data;
  const jobs = (Array.isArray(d) ? d : d?.jobs ?? []) as { id: number; title?: string }[];
  return jobs.map(j => ({
    key: `docs-job-${j.id}`,
    label: j.title || `Job ${j.id}`,
    href: '/documents',
    loader: async () => {
      const dr = await apiClient.get(`/jobs/${j.id}/documents`);
      return list(dr.data).map(doc => ({
        key: `docs-job-${j.id}-f-${doc.id}`,
        label: String(doc.label || doc.file_name || `File ${doc.id}`),
        externalHref: `/${String(doc.file_path || '').replace(/^\//, '')}`,
      }));
    },
  }));
}

export const TREE_ROOT_LOADERS: Record<string, () => Promise<TreeNode[]>> = {
  '/clients': loadClients,
  '/jobs': loadJobs,
  '/employees': loadEmployees,
  '/crews': loadCrews,
  '/quotes': loadQuotes,
  '/invoices': loadInvoices,
  '/agreements': loadAgreements,
  '/vendors': loadVendors,
  '/purchase-orders': loadPurchaseOrders,
  '/documents': loadDocumentFolders,
};

interface TreeRowProps {
  node: TreeNode;
  depth: number;
  onNavigate?: () => void;
  dark?: boolean;
  /** Desktop tray: open this node's page as a floating window. */
  onPopOut?: (href: string, label: string) => void;
  /** Immediate parent's label — gives pins context ("WilcoxTruss · Invoices"). */
  parentLabel?: string;
}

function TreeRow({ node, depth, onNavigate, dark, onPopOut, parentLabel }: TreeRowProps) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [kids, setKids] = useState<TreeNode[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const pins = usePins();
  const pinned = !!node.href && pins.some(p => p.path === node.href);
  const pinLabel = parentLabel ? `${parentLabel} · ${node.label}` : node.label;

  const toggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && !kids && node.loader) {
      try { setKids(await node.loader()); }
      catch { setLoadFailed(true); }
    }
  };

  const active = node.href && (pathname === node.href || pathname.startsWith(node.href + '/'));
  const rowCls = dark
    ? `${active ? 'text-[#C77D3F] font-semibold' : 'text-gray-300'} hover:bg-white/10`
    : `${active ? 'text-primary font-semibold' : 'text-gray-600'} hover:bg-gray-50`;
  const chevronCls = dark ? 'text-gray-500 hover:text-[#C77D3F]' : 'text-gray-300 hover:text-primary';
  const dimCls = dark ? 'text-gray-500' : 'text-gray-300';

  // Touch-friendly in the light (mobile drawer) variant; compact in the tray.
  const padY = dark ? 'py-1' : 'py-2';
  const chevPad = dark ? 'p-1' : 'p-2';

  return (
    <div>
      <div
        className={`flex items-center rounded-lg ${dark ? 'text-[13px]' : 'text-sm'} ${rowCls}`}
        style={{ paddingLeft: depth * 14 }}
      >
        {node.loader ? (
          <button onClick={toggle} className={`${chevPad} flex-shrink-0 ${chevronCls}`} title={open ? 'Collapse' : 'Expand'}>
            <span className="material-symbols-outlined text-base leading-none">{open ? 'expand_more' : 'chevron_right'}</span>
          </button>
        ) : (
          <span className={`${dark ? 'w-6' : 'w-8'} flex-shrink-0`} />
        )}
        {node.externalHref ? (
          <a href={node.externalHref} target="_blank" rel="noopener noreferrer" onClick={onNavigate}
            className={`flex-1 min-w-0 flex items-center gap-1 truncate ${padY} pr-2`}>
            <span className="truncate">{node.label}</span>
            <span className={`material-symbols-outlined text-xs flex-shrink-0 ${dimCls}`}>open_in_new</span>
          </a>
        ) : node.href ? (
          <Link
            to={node.href}
            // #190: folder-style rows (they have children) expand in place on
            // click — double-click opens the folder's own page as a window.
            // #182 still applies to leaves: the click IS the pop-out.
            onClick={e => {
              if (node.loader) {
                void toggle(e);
              } else if (onPopOut) {
                e.preventDefault();
                onPopOut(node.href!, pinLabel);
                onNavigate?.();
              } else {
                onNavigate?.();
              }
            }}
            onDoubleClick={e => {
              if (node.loader && onPopOut) {
                e.preventDefault();
                e.stopPropagation();
                onPopOut(node.href!, pinLabel);
                onNavigate?.();
              }
            }}
            className={`flex-1 min-w-0 truncate ${padY} pr-2`}
          >
            {node.label}
            {node.sub ? <span className={`ml-1 text-[11px] ${dimCls}`}>{node.sub}</span> : null}
          </Link>
        ) : (
          <span className={`flex-1 min-w-0 truncate ${padY} pr-2`}>{node.label}</span>
        )}
        {node.href && (
          <button
            onClick={e => { e.preventDefault(); e.stopPropagation(); togglePin({ path: node.href!, label: pinLabel }); }}
            title={pinned ? 'Unpin' : 'Pin for quick access'}
            className={`${chevPad} flex-shrink-0 ${pinned ? 'text-primary' : `${dimCls} ${dark ? 'hover:text-[#C77D3F]' : 'hover:text-primary'}`}`}
          >
            <span className="material-symbols-outlined text-sm leading-none" style={pinned ? { fontVariationSettings: "'FILL' 1" } : undefined}>
              push_pin
            </span>
          </button>
        )}
      </div>
      {open && (
        <div>
          {!kids && !loadFailed ? (
            <p className={`text-[11px] py-0.5 ${dimCls}`} style={{ paddingLeft: (depth + 1) * 14 + 8 }}>Loading…</p>
          ) : loadFailed ? (
            <p className={`text-[11px] py-0.5 ${dimCls}`} style={{ paddingLeft: (depth + 1) * 14 + 8 }}>couldn't load</p>
          ) : kids!.length === 0 ? (
            <p className={`text-[11px] italic py-0.5 ${dimCls}`} style={{ paddingLeft: (depth + 1) * 14 + 8 }}>empty</p>
          ) : kids!.map(k => <TreeRow key={k.key} node={k} depth={depth + 1} onNavigate={onNavigate} dark={dark} onPopOut={onPopOut} parentLabel={node.label} />)}
        </div>
      )}
    </div>
  );
}

/** Expandable sub-tree rendered directly under a main-nav entity item.
 *  `dark` restyles for the taskbar tray; `searchable` adds a root-level filter. */
export function NavSubTree({ rootHref, onNavigate, dark, searchable, onPopOut }: {
  rootHref: string; onNavigate?: () => void; dark?: boolean; searchable?: boolean;
  onPopOut?: (href: string, label: string) => void;
}) {
  const [kids, setKids] = useState<TreeNode[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    let alive = true;
    const loader = TREE_ROOT_LOADERS[rootHref];
    if (!loader) return;
    loader().then(k => { if (alive) setKids(k); }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [rootHref]);

  const dimCls = dark ? 'text-gray-500' : 'text-gray-300';
  if (!TREE_ROOT_LOADERS[rootHref]) return null;
  if (failed) return <p className={`text-[11px] pl-9 py-0.5 ${dimCls}`}>couldn't load</p>;
  if (!kids) return <p className={`text-[11px] pl-9 py-0.5 ${dimCls}`}>Loading…</p>;

  const needle = q.trim().toLowerCase();
  const shown = needle ? kids.filter(k => k.label.toLowerCase().includes(needle)) : kids;

  return (
    <div className={dark ? 'ml-6 mr-1 mb-1 rounded-xl bg-black/20 border border-white/5 p-1.5' : 'ml-3 border-l border-gray-100 pl-1 mb-1'}>
      {searchable && kids.length > 6 && (
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Filter…"
          className={dark
            ? 'w-full h-8 px-2 mb-1 rounded-lg bg-white/5 text-sm text-gray-200 placeholder-gray-500 border border-white/10 focus:outline-none focus:border-[#C77D3F]/50'
            : 'w-full h-8 px-2 mb-1 rounded-lg bg-gray-50 text-sm text-gray-700 placeholder-gray-400 border border-gray-200 focus:outline-none focus:border-primary/50'}
        />
      )}
      <div className={dark ? 'max-h-64 overflow-y-auto overscroll-contain' : undefined}>
        {shown.length === 0
          ? <p className={`text-[11px] italic pl-6 py-0.5 ${dimCls}`}>{needle ? 'no matches' : 'empty'}</p>
          : shown.map(k => <TreeRow key={k.key} node={k} depth={0} onNavigate={onNavigate} dark={dark} onPopOut={onPopOut} />)}
      </div>
    </div>
  );
}
